// ElectionMode/ElectionModeList.js
import React, { useEffect, useState } from "react";
import { Table, Button, Space, Tag, message } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useAxiosInstance from "../../lib/useAxiosInstance";
import DeleteModal from "../DeleteModal";

const ElectionModeList = ({ refreshKey }) => {
  const axiosInstance = useAxiosInstance();
  const navigate = useNavigate();

  const [electionModes, setElectionModes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [selectedElectionMode, setSelectedElectionMode] = useState(null);

  const fetchElectionModes = async () => {
    try {
      setLoading(true);

      const res = await axiosInstance.get(
        "api/electionMode"
      );

      const list = res.data?.data || [];
      setElectionModes(list);
    } catch (error) {
      console.error(error);
      message.error("Failed to load election data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchElectionModes();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleView = (record) => {
    navigate(`/electionMode/${record._id}`);
  };

  const handleEdit = (record) => {
    navigate(`/electionMode/${record._id}/edit`);
  };

  const openDeleteModal = (record) => {
    setSelectedElectionMode(record);
    setIsDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setSelectedElectionMode(null);
    setIsDeleteModal(false);
  };

  const confirmDelete = async () => {
    if (!selectedElectionMode) return;

    try {
      await axiosInstance.delete(
        `api/electionMode/${selectedElectionMode._id}`
      );

      message.success("Election data deleted successfully");

      fetchElectionModes();
    } catch (error) {
      console.error(error);
      message.error("Failed to delete election data");
    } finally {
      closeDeleteModal();
    }
  };

  const columns = [
    {
      title: "Files",
      key: "files",
      width: 120,
      render: (_, record) =>
        `${record.form20Files?.length || 0} Form20 / ${
          record.erFiles?.length || 0
        } ER`,
    },
    {
      title: "Name",
      dataIndex: "electionModeName",
      key: "electionModeName",
    },
    {
      title: "Type",
      dataIndex: "electionModeType",
      key: "electionModeType",
      render: (type) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      render: (client) => {
        if (!client) return null;
        const label =
          `${client.adminFirstName || ""} ${client.adminLastName || ""}`.trim() ||
          client.organisationName ||
          client.adminEmail ||
          client._id;
        return <Tag color="blue">{label}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleView(record);
            }}
          >
            View
          </Button>

          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              openDeleteModal(record);
            }}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="survey-list-page">

        <Table
          rowKey="_id"
          loading={loading}
          dataSource={electionModes}
          columns={columns}
          onRow={(record) => ({
            onClick: () => handleView(record),
          })}
        />
      </div>

      <DeleteModal
        isDeleteModal={isDeleteModal}
        showModalDelete={closeDeleteModal}
        handleDelete={confirmDelete}
        textheading="Delete Election Data"
        action="delete"
        deleteTitle={selectedElectionMode?.electionModeName}
        deleteBtn="Delete"
      />
    </>
  );
};

export default ElectionModeList;
